import React, { useState, useEffect } from 'react';
import { useData } from '../context/DataContext';
import { Nutritionist } from '../types';
import Modal from './shared/Modal';
import Button from './shared/Button';
import { UtensilsCrossed, Menu, X, LogOut, Key, User, Shield, AlertTriangle, Eye, EyeOff, CheckCircle2, Loader2, AlertCircle, Phone, Mail, Edit2, Cloud, CloudOff } from 'lucide-react';

interface HeaderProps {
  onToggleSidebar: () => void;
  isSidebarOpen: boolean;
}

const Header: React.FC<HeaderProps> = ({ onToggleSidebar, isSidebarOpen }) => {
  const { nutritionist, setNutritionist, logout, currentUser, updateUser, isSyncing, lastSync, isCloudConfigured } = useData();
  const [isModalOpen, setIsModalOpen] = useState(false);
  
  const [formData, setFormData] = useState<Nutritionist>(
    nutritionist || { title: 'Dra.', name: '', crn: '', whatsapp: '' }
  );

  const [showPasswordFields, setShowPasswordFields] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isUpdatingPassword, setIsUpdatingPassword] = useState(false);
  
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [passwordSuccess, setPasswordSuccess] = useState<string | null>(null);

  // Estados para E-mail de Recuperação (Admin)
  const [isEditingRecovery, setIsEditingRecovery] = useState(false);
  const [recoveryEmailInput, setRecoveryEmailInput] = useState(currentUser?.recoveryEmail || '');

  useEffect(() => {
    if (nutritionist) setFormData(nutritionist);
    if (currentUser) setRecoveryEmailInput(currentUser.recoveryEmail || '');
  }, [nutritionist, currentUser, isModalOpen]);

  const handleSaveProfile = () => {
    setNutritionist(formData);
    if (!showPasswordFields && !isEditingRecovery) {
      setIsModalOpen(false);
    }
  };

  const handleUpdatePassword = async () => {
    setPasswordError(null);
    setPasswordSuccess(null);
    
    if (!newPassword || !confirmPassword) {
      setPasswordError("Por favor, preencha todos os campos de senha.");
      return;
    }

    if (newPassword !== confirmPassword) {
      setPasswordError("As senhas digitadas não coincidem.");
      return;
    }

    if (newPassword.length < 6) {
      setPasswordError("A senha deve ter pelo menos 6 caracteres.");
      return;
    }

    if (!currentUser) return;

    setIsUpdatingPassword(true);
    try {
      await updateUser({ ...currentUser, password: newPassword, isPasswordReset: false });
      setPasswordSuccess("Senha alterada com sucesso!");
      setNewPassword('');
      setConfirmPassword('');
      setTimeout(() => {
        setPasswordSuccess(null);
        setShowPasswordFields(false);
      }, 3000);
    } catch (error: any) {
      setPasswordError("Erro ao salvar senha.");
    } finally {
      setIsUpdatingPassword(false);
    }
  };

  const handleSaveRecoveryEmail = async () => {
    if (!currentUser) return;
    try {
      await updateUser({ ...currentUser, recoveryEmail: recoveryEmailInput });
      setIsEditingRecovery(false);
    } catch (error) {
      console.error("Erro ao salvar email de recuperação", error);
    }
  };

  return (
    <>
      <header className="bg-white border-b border-slate-200 z-30 sticky top-0 h-20 flex items-center">
        <div className="px-4 sm:px-6 flex justify-between items-center w-full">
          <div className="flex items-center gap-4">
            <button onClick={onToggleSidebar} className="p-2 rounded-lg text-slate-500 hover:bg-slate-100 transition-colors">
              {isSidebarOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
            <div className="flex items-center gap-2">
              <div className="bg-indigo-600 p-1.5 rounded-lg"><UtensilsCrossed className="text-white" size={18} /></div>
              <h1 className="text-lg font-bold text-indigo-600 hidden sm:block">4Nutri</h1>
              {currentUser?.role === 'admin' && <span className="bg-indigo-100 text-indigo-600 text-[10px] font-black px-2 py-0.5 rounded-full ml-2">ADMIN</span>}
              
              {/* Indicador de Sync */}
              <div className="flex items-center gap-2 ml-4 px-3 py-1 rounded-full bg-slate-50 border border-slate-100">
                 {isSyncing ? (
                   <>
                     <Loader2 size={12} className="animate-spin text-indigo-500"/>
                     <span className="text-[10px] text-indigo-500 font-bold uppercase tracking-tighter">Salvando...</span>
                   </>
                 ) : lastSync && isCloudConfigured ? (
                   <>
                     <Cloud size={12} className="text-green-500"/>
                     <span className="text-[10px] text-green-600 font-bold uppercase tracking-tighter">
                       Cloud {lastSync.toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}
                     </span>
                   </>
                 ) : (
                   <>
                     <CloudOff size={12} className="text-slate-400"/>
                     <span className="text-[10px] text-slate-400 font-bold uppercase tracking-tighter">Offline</span>
                   </>
                 )}
              </div>
            </div>
          </div>

          <div className="flex items-center gap-4">
            {nutritionist || currentUser?.role === 'admin' ? (
              <div className="flex items-center gap-3">
                <div className="hidden sm:block text-right border-r border-slate-100 pr-4">
                  <p className="text-sm font-bold text-slate-800">{currentUser?.role === 'admin' ? 'Administrador' : `${formData.title} ${formData.name || 'Nutricionista'}`}</p>
                  <p className="text-[10px] text-slate-400 font-bold uppercase">{currentUser?.role === 'admin' ? 'Gestão Total' : (formData.crn || 'Sem CRN')}</p>
                </div>
                <button onClick={() => { setShowPasswordFields(false); setPasswordError(null); setIsEditingRecovery(false); setIsModalOpen(true); }} className="w-10 h-10 bg-indigo-50 rounded-full flex items-center justify-center text-indigo-600 hover:bg-indigo-100 transition-colors">
                  <User size={18} />
                </button>
              </div>
            ) : (
              <Button onClick={() => setIsModalOpen(true)} size="sm">Configurar Perfil</Button>
            )}
            <button onClick={logout} className="p-2 text-slate-400 hover:text-red-500 transition-colors"><LogOut size={20} /></button>
          </div>
        </div>
      </header>

      <Modal isOpen={isModalOpen} onClose={() => !isUpdatingPassword && setIsModalOpen(false)} title="Configurações de Perfil e Conta" size="lg">
        <div className="space-y-8">
          <section className="space-y-4">
            <h3 className="text-xs font-black text-slate-400 uppercase tracking-widest flex items-center gap-2"><User size={14} /> Identificação Profissional</h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-[10px] font-bold text-slate-500 uppercase mb-1 flex items-center gap-1"><Mail size={10}/> Email de cadastro</label>
                <input 
                  type="text" 
                  value={currentUser?.email || ''} 
                  readOnly 
                  className="block w-full bg-slate-100 border-slate-200 rounded-xl p-2.5 text-sm border text-slate-500 cursor-not-allowed outline-none" 
                />
              </div>
              <div className="grid grid-cols-4 gap-2">
                <div className="col-span-1">
                  <label className="block text-[10px] font-bold text-slate-500 uppercase mb-1">Tratamento</label>
                  <select value={formData.title} onChange={(e) => setFormData({ ...formData, title: e.target.value })} className="block w-full bg-slate-50 border-slate-200 rounded-xl p-2.5 text-sm border focus:ring-2 focus:ring-indigo-500 outline-none">
                    <option>Dra.</option><option>Dr.</option><option>Nutri</option>
                  </select>
                </div>
                <div className="col-span-3">
                  <label className="block text-[10px] font-bold text-slate-500 uppercase mb-1">Nome Completo</label>
                  <input type="text" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} className="block w-full bg-slate-50 border-slate-200 rounded-xl p-2.5 text-sm border focus:ring-2 focus:ring-indigo-500 outline-none" placeholder="Seu nome profissional" />
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-[10px] font-bold text-slate-500 uppercase mb-1">CRN</label>
                <input type="text" value={formData.crn} onChange={(e) => setFormData({ ...formData, crn: e.target.value })} className="block w-full bg-slate-50 border-slate-200 rounded-xl p-2.5 text-sm border focus:ring-2 focus:ring-indigo-500 outline-none" placeholder="Ex: CRN-3 12345" />
              </div>
              <div>
                <label className="block text-[10px] font-bold text-slate-500 uppercase mb-1 flex items-center gap-1"><Phone size={10}/> WhatsApp (Para Agendamentos)</label>
                <input type="text" value={formData.whatsapp} onChange={(e) => setFormData({ ...formData, whatsapp: e.target.value.replace(/\D/g, '') })} className="block w-full bg-slate-50 border-slate-200 rounded-xl p-2.5 text-sm border focus:ring-2 focus:ring-indigo-500 outline-none" placeholder="Apenas números com DDD" />
                <p className="text-[9px] text-slate-400 mt-1 italic">Necessário para a página pública de agendamento.</p>
              </div>
            </div>
            <div className="flex justify-end">
              <Button onClick={handleSaveProfile} size="sm" variant="secondary">Salvar Identificação</Button>
            </div>
          </section>

          <section className="space-y-4 border-t border-slate-100 pt-6">
            <h3 className="text-xs font-black text-slate-400 uppercase tracking-widest flex items-center gap-2"><Shield size={14} /> Configuração de Conta</h3>
            
            <div className="flex flex-wrap gap-3">
              {!showPasswordFields ? (
                <button type="button" onClick={() => setShowPasswordFields(true)} className="flex items-center gap-2 text-indigo-600 text-xs font-bold hover:bg-indigo-50 px-4 py-2 rounded-xl transition-all">
                  <Key size={14} /> Alterar senha da conta
                </button>
              ) : (
                <div className="w-full space-y-4 p-5 bg-slate-50 rounded-2xl border border-slate-200">
                  {passwordError && <div className="p-3 bg-red-50 border border-red-100 rounded-xl text-red-600 text-xs flex items-center gap-3"><AlertCircle size={16}/><span className="font-bold">{passwordError}</span></div>}
                  {passwordSuccess && <div className="p-3 bg-green-50 border border-green-100 rounded-xl text-green-700 text-xs flex items-center gap-3"><CheckCircle2 size={16}/><span className="font-bold">{passwordSuccess}</span></div>}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="relative">
                      <label className="block text-[10px] font-bold text-slate-400 uppercase mb-1">Nova Senha</label>
                      <input type="text" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} className="w-full bg-white border-slate-200 rounded-xl p-2.5 text-sm outline-none text-slate-800" placeholder="Mínimo 6 caracteres" />
                      <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-7 text-slate-300">
                        {showPassword ? <EyeOff size={16}/> : <Eye size={16}/>}
                      </button>
                    </div>
                    <div>
                      <label className="block text-[10px] font-bold text-slate-400 uppercase mb-1">Repetir Nova Senha</label>
                      <input type="text" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} className="w-full bg-white border-slate-200 rounded-xl p-2.5 text-sm outline-none text-slate-800" />
                    </div>
                  </div>
                  <div className="flex justify-end mt-4">
                    <Button size="sm" onClick={handleUpdatePassword} disabled={isUpdatingPassword}>{isUpdatingPassword ? 'Salvando...' : 'Confirmar Nova Senha'}</Button>
                  </div>
                </div>
              )}

              {/* Email de Recuperação - Apenas para Admin */}
              {currentUser?.role === 'admin' && (
                <div className="w-full pt-2">
                  {!isEditingRecovery && !currentUser.recoveryEmail ? (
                    <button type="button" onClick={() => setIsEditingRecovery(true)} className="flex items-center gap-2 text-indigo-600 text-xs font-bold hover:bg-indigo-50 px-4 py-2 rounded-xl transition-all">
                      <Mail size={14} /> Cadastrar email de recuperação de senha
                    </button>
                  ) : !isEditingRecovery && currentUser.recoveryEmail ? (
                    <div className="flex items-center justify-between p-4 bg-slate-50 border border-slate-200 rounded-2xl w-full">
                       <div className="flex items-center gap-3">
                         <div className="p-2 bg-indigo-100 text-indigo-600 rounded-lg">
                           <Mail size={16} />
                         </div>
                         <div>
                            <p className="text-[10px] font-bold text-slate-400 uppercase">Email de recuperação</p>
                            <p className="text-sm font-bold text-slate-700">{currentUser.recoveryEmail}</p>
                         </div>
                       </div>
                       <button onClick={() => setIsEditingRecovery(true)} className="p-2 text-slate-400 hover:text-indigo-600 transition-colors">
                          <Edit2 size={16} />
                       </button>
                    </div>
                  ) : (
                    <div className="w-full space-y-4 p-5 bg-slate-50 rounded-2xl border border-indigo-200 animate-in fade-in">
                       <div>
                          <label className="block text-[10px] font-bold text-indigo-500 uppercase mb-1">Digite o email para recuperação</label>
                          <input 
                            type="email" 
                            value={recoveryEmailInput} 
                            onChange={(e) => setRecoveryEmailInput(e.target.value)}
                            className="w-full bg-white border border-indigo-200 rounded-xl p-3 text-sm outline-none focus:ring-2 focus:ring-indigo-500" 
                            placeholder="exemplo@email.com"
                          />
                       </div>
                       <div className="flex justify-end gap-2">
                          <button onClick={() => setIsEditingRecovery(false)} className="px-4 py-2 text-xs font-bold text-slate-400 hover:text-slate-600">Cancelar</button>
                          <Button size="sm" onClick={handleSaveRecoveryEmail}>Salvar E-mail</Button>
                       </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </section>
        </div>
      </Modal>
    </>
  );
};

export default Header;